import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import {
    KqlActivity,
    KustoDatabaseLinkedService,
    LinkedService,
    IModel,
    Metamodel,
    ReferenceAdapter,
    WorkspaceBuildInLinkedService,
    AzureDataExplorerLinkedService,
    isLogicAppExpression,
    SharedModel,
    ModelConstants,
    ConnectionKind
} from 'shell-models-lib';
import { ValueAdapter } from './../value.adapter';
import { Owned } from 'app/model/base/named';
import { merge, isEmpty } from 'lodash';
import { LinkedServiceAdapter } from '../linkedservices/linkedservice.adapter';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import {
    ActivityConnectionContext,
    IKustoQueryLanguageActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ModelService } from 'app/model/services/model.service';
import { LoggerService } from 'app/logger/logger.service';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { IFrameService } from 'app/services/iframe.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(KqlActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        linkedService: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        typeProperties: {
            command: '*',
            commandTimeout: '*',
            database: '*'
        },
        externalReferences: '*'
    }
})
export class KqlActivityAdapter extends ActivityAsyncAdapter<
    KqlActivity,
    Model.KqlActivity,
    IKustoQueryLanguageActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(KqlActivity);
    }

    public override async toFabricCopilotContext(activity: KqlActivity): Promise<IKustoQueryLanguageActivityContext> {
        if (activity) {
            const activityContext: IKustoQueryLanguageActivityContext = {
                ...(await super.toFabricCopilotContext(activity)),
                Command: ValueAdapter.toResource(activity, KqlActivity.COMMAND, true)
            };
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
                let database;
                if (
                    (database = ValueAdapter.toResource(activity, KqlActivity.DATABASE)) &&
                    activityContext.Connection
                ) {
                    activityContext.Connection.Database = database;
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IKustoQueryLanguageActivityContext,
        model?: KqlActivity
    ): Promise<KqlActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Command) {
                ValueAdapter.toModel(model, KqlActivity.COMMAND, context.Command);
            }
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext<AzureDataExplorerLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                            LinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.linkedService);
                    ValueAdapter.toModel(model, KqlActivity.DATABASE, context.Connection.Database);
                }
            }
        }
        return model;
    }

    protected async _toResource(activity: KqlActivity): Promise<Model.KqlActivity> {
        const modelKqlActivity: Model.KqlActivity = super._getCommonActivityProperties(
            activity,
            'KustoQueryLanguage'
        ) as Model.KqlActivity;
        const kqlActivity: KqlActivity = activity as KqlActivity;

        modelKqlActivity.typeProperties = {
            command: ValueAdapter.toResource(kqlActivity, KqlActivity.COMMAND),
            commandTimeout: ValueAdapter.toResource(kqlActivity, KqlActivity.COMMAND_TIMEOUT),
            database: !activity.linkedService?.isWorkspaceBuiltIn
                ? ValueAdapter.toResource(kqlActivity, KqlActivity.DATABASE)
                : undefined
        };

        if (kqlActivity.linkedService) {
            if (activity.linkedService.isWorkspaceBuiltIn) {
                const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(activity.linkedService));
                const linkedService: Model.LinkedServiceResource = await linkedServiceAdapter.toResource(
                    activity.linkedService
                );
                modelKqlActivity.linkedService = linkedService;
            } else {
                modelKqlActivity.linkedServiceName = this._toLinkedServiceReferenceModel(kqlActivity.linkedService);
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            merge(modelKqlActivity, parsed);
        }

        return modelKqlActivity;
    }

    protected async _toModel(modelActivity: Model.KqlActivity): Promise<KqlActivity> {
        const activity: KqlActivity = KqlActivity.MODEL.create<KqlActivity>(modelActivity.name);
        const modelKqlActivity = modelActivity as Model.KqlActivity;

        const logger = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');

        this._updatedCommonProperties(modelActivity, activity);

        if (modelKqlActivity.typeProperties) {
            ValueAdapter.toModel(activity, KqlActivity.COMMAND, modelKqlActivity.typeProperties.command);
            ValueAdapter.toModel(activity, KqlActivity.COMMAND_TIMEOUT, modelKqlActivity.typeProperties.commandTimeout);
            ValueAdapter.toModel(activity, KqlActivity.DATABASE, modelKqlActivity.typeProperties.database);
        }

        let linkedServiceModel: IModel;
        let linkedServiceName: Model.LinkedServiceReference = modelKqlActivity.linkedServiceName;
        if (!isEmpty(modelKqlActivity.linkedService)) {
            const linkedServiceType = modelKqlActivity.linkedService.properties?.type;
            linkedServiceModel = Metamodel.modelByName(linkedServiceType);
            const linkedServiceAdapter = this.context.asyncAdapterFor(
                linkedServiceModel
            ) as LinkedServiceAdapter<KustoDatabaseLinkedService>;
            // Handle workspace built-in linked service in dynamic content
            const lsModel: WorkspaceBuildInLinkedService = Owned.create<WorkspaceBuildInLinkedService>(
                linkedServiceModel,
                activity
            );
            await linkedServiceAdapter?.toModel(modelKqlActivity.linkedService, lsModel);
            if (lsModel?.parameterized) {
                activity.linkedService = lsModel;
            } else {
                linkedServiceName = {
                    type: 'LinkedServiceReference',
                    referenceName: linkedServiceAdapter.getArtifactId(modelKqlActivity.linkedService),
                    workspaceObjectId: linkedServiceAdapter.getWorkspaceObjectId(modelKqlActivity.linkedService)
                };
            }
        }

        if (linkedServiceName != null) {
            if (isLogicAppExpression((linkedServiceName as SharedModel.LinkedServiceReference).referenceName)) {
                const linkedServiceType: string = ModelConstants.azureDataExplorerLinkedService;
                const lsModel = Metamodel.modelByName(linkedServiceType);
                const templateLinkedService = Owned.create<LinkedService>(lsModel, activity);
                templateLinkedService.name = generateUUID().replaceAll('-', '_');
                ValueAdapter.toModel(
                    templateLinkedService,
                    Metamodel.modelByName(linkedServiceType).attribute('objectId'),
                    (linkedServiceName as SharedModel.LinkedServiceReference).referenceName
                );
                templateLinkedService.tridentArtifactType = templateLinkedService.tridentArtifactType ?? 'Connection';

                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                templateLinkedService.tridentConnectionKind = (iframeService.currentCustomerProfile.linkedServiceToConnectionTypeMapping[linkedServiceType] || linkedServiceType) as ConnectionKind;
                templateLinkedService.parameterized = true;
                activity.linkedService = templateLinkedService;
                logger.log('kql-activity.adapter', 'linkedService is parameterized', { linkedServiceType });
            } else {
                activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    linkedServiceModel ?? AzureDataExplorerLinkedService.MODEL,
                    linkedServiceName,
                    this.context
                );
            }
        }
        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelKqlActivity, KqlActivity.MODEL));

        return activity;
    }
}
